import { ASSESSMENT_RESULT } from '@/utils/const/products/vod-assessment';
import { Col, Divider, Input, InputNumber, Row } from 'antd';
import { Radio } from 'antd';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  formatPercentNumber,
  formatCost,
  formatCostUnit,
  translateMonthToYear,
  translateYearToMonth,
} from '../../../../utils/functions/vod-assessment';
import LineChart from '../../../echars/LineChart';
import Styles from '../index.module.scss';
import IndicatorCard from './IndicatorCard';
import NumberInput from './NumberInput';

enum FeeTimeOptions {
  month = 'month',
  longTime = 'longTime',
}

const feeTimeOptions = [
  {
    label: ASSESSMENT_RESULT.feeTimeOptionTexts[0],
    value: FeeTimeOptions.month,
  },
  {
    label: ASSESSMENT_RESULT.feeTimeOptionTexts[1],
    value: FeeTimeOptions.longTime,
  },
];

const SchemeCard = ({ text, selected, onChange, value }) => {
  return (
    <div
      className={`${Styles.scheme} ${
        selected ? Styles.schemeSelected : Styles.schemeNotSelected
      }`}
      onClick={() => {
        if (!selected) {
          onChange(value);
        }
      }}
    >
      {text}
    </div>
  );
};

interface IIndicatorCard {
  saveRate: number;
  earning: number;
}
export interface IAssessment extends IIndicatorCard {
  originCost: number;
  targetCost: number;
  originCostCdn?: number;
  originCostTranscode?: number;
  originCostStorage?: number;
  targetCostCdn?: number;
  targetCostTranscode?: number;
  targetCostStorage?: number;
}

interface IProps {
  assessmentTwo: IAssessment;
  assessmentOne: IAssessment;
}

const AssessmentResult = ({ assessmentTwo, assessmentOne }: IProps, ref) => {
  const [scheme, setScheme] = useState(1);
  const [increaseRate, setIncreaseRate] = useState(10); // 年业务增加默认 10%
  const [period, setPeriod] = useState(3); // 周期 3 年
  const [currentFeeTime, setCurrentFeeTime] = useState(FeeTimeOptions.month);

  const [chartData, setChartData] = useState({});
  // 指标卡
  const [schemeIndicator, setSchemeIndicator] = useState({} as IIndicatorCard);

  // 获取业务增长率列表
  const getIncreaseList = (period, increaseRate, pow = 1) => {
    const time = period * 12;
    const increaseList = [];
    for (let i = 0; i < time; i++) {
      if (i === 0) {
        increaseList.push(1);
      } else {
        increaseList.push(
          increaseList[i - 1] * Math.pow(1 + increaseRate / 100, pow)
        );
      }
    }
    return increaseList;
  };

  useEffect(() => {
    const monthIncreaseRate = increaseRate / 12; // 单位 %
    // 每当「年业务增长」和「评估周期发生」变化时，重新计算 increaseList
    // 转码、存储、cdn，方案二 的增长列表，其中 cdn和方案二的增长 pow = 2
    const increaseList = getIncreaseList(period, monthIncreaseRate);
    const pow2IncreaseList = getIncreaseList(period, monthIncreaseRate, 2);
    // 长周期 线图
    if (currentFeeTime === FeeTimeOptions.longTime) {
      const commonData = [];
      const zzqlData = [];
      if (scheme === 1) {
        // 方案 1
        increaseList.forEach((item, index) => {
          const time = translateMonthToYear(index + 1);
          const commonCost =
            (assessmentOne.originCostTranscode +
              assessmentOne.originCostStorage) *
              item +
            assessmentOne.originCostCdn * pow2IncreaseList[index];
          const zzqlCost =
            (assessmentOne.targetCostTranscode +
              assessmentOne.targetCostStorage) *
              item +
            assessmentOne.targetCostCdn * pow2IncreaseList[index];
          commonData.push({ time, cost: commonCost });
          zzqlData.push({ time, cost: zzqlCost });
        });
      } else {
        // 方案 2
        pow2IncreaseList.forEach((item, index) => {
          const time = translateMonthToYear(index + 1);
          // const commonCost = assessmentTwo.originCost * item;
          const commonCost =
            (assessmentOne.originCostTranscode +
              assessmentOne.originCostStorage) *
              increaseList[index] +
            assessmentOne.originCostCdn * item;
          const zzqlCost = assessmentTwo.targetCost * item;
          commonData.push({ time, cost: commonCost });
          zzqlData.push({ time, cost: zzqlCost });
        });
      }
      const chartDataTemp = {
        common: commonData,
        zzql: zzqlData,
      };
      setChartData(chartDataTemp);
      // 长周期 取累加
      const commonTotal = chartDataTemp?.common.reduce(
        (total, item) => (total += item.cost),
        0
      );
      const zzqlTotal = chartDataTemp?.zzql.reduce(
        (total, item) => (total += item.cost),
        0
      );
      const earning = commonTotal - zzqlTotal;
      setSchemeIndicator({
        earning,
        saveRate: earning / commonTotal,
      });
    } else {
      // 按月计算
      setSchemeIndicator({
        earning: scheme === 1 ? assessmentOne.earning : assessmentTwo.earning,
        saveRate:
          scheme === 1 ? assessmentOne.saveRate : assessmentTwo.saveRate,
      });
    }
  }, [
    scheme,
    assessmentOne,
    assessmentTwo,
    currentFeeTime,
    period,
    increaseRate,
  ]);

  function formatStringCost(val: string, defaultUnit: string) {
    return {
      value: parseInt(val),
      unit: val.replace(/[-\d]/g, '') || defaultUnit,
    };
  }

  return (
    <div className={Styles.resultContainer}>
      <Row>
        <Col span="12">
          <SchemeCard
            text={ASSESSMENT_RESULT.schemeOne.label}
            selected={scheme === 1}
            onChange={setScheme}
            value={1}
          />
        </Col>
        <Col span="12">
          <SchemeCard
            text={ASSESSMENT_RESULT.schemeTwo.label}
            selected={scheme === 2}
            onChange={setScheme}
            value={2}
          />
        </Col>
      </Row>
      <div className={Styles.result}>
        <div className={Styles.resultDesc}>
          {scheme === 1
            ? ASSESSMENT_RESULT.schemeOne.desc
            : ASSESSMENT_RESULT.schemeTwo.desc}
        </div>
        <div className={Styles.feeTimeContainer}>
          <Radio.Group
            options={feeTimeOptions}
            value={currentFeeTime}
            onChange={(val) => setCurrentFeeTime(val.target.value)}
          />
          {currentFeeTime === FeeTimeOptions.longTime && (
            <Row className={Styles.periodForm}>
              <Col style={{ marginRight: 24 }}>
                <span className={Styles.inputLabel}>
                  {ASSESSMENT_RESULT.schemeTwo.increaseForm.increaseRate.label}
                </span>
                <NumberInput
                  className={Styles.numberInput}
                  suffix={
                    ASSESSMENT_RESULT.schemeTwo.increaseForm.increaseRate.suffix
                  }
                  min={0}
                  max={1000}
                  precision={0}
                  value={increaseRate}
                  onChange={(e) =>
                    e ? setIncreaseRate(e) : setIncreaseRate(0)
                  }
                />
              </Col>
              <Col>
                <span className={Styles.inputLabel}>
                  {ASSESSMENT_RESULT.schemeTwo.increaseForm.period.label}
                </span>
                <NumberInput
                  className={Styles.numberInput}
                  suffix={
                    ASSESSMENT_RESULT.schemeTwo.increaseForm.period.suffix
                  }
                  min={0}
                  max={30}
                  precision={0}
                  value={period}
                  onChange={(e) => (e ? setPeriod(e) : setPeriod(0))}
                />
              </Col>
            </Row>
          )}
        </div>
        <Row gutter={40}>
          <Col>
            <IndicatorCard
              value={formatPercentNumber(schemeIndicator.saveRate)}
              unit={ASSESSMENT_RESULT.saveRate.unit}
              desc={ASSESSMENT_RESULT.saveRate.label}
              type={schemeIndicator.saveRate <= 0 ? 'up' : 'down'}
            />
          </Col>
          <Col>
            <Divider type="vertical" style={{ height: 80, top: 10 }} />
          </Col>
          <Col>
            <IndicatorCard
              {...formatStringCost(
                formatCostUnit(schemeIndicator.earning, 0),
                currentFeeTime === FeeTimeOptions.month
                  ? ASSESSMENT_RESULT.earning.unit[0]
                  : ASSESSMENT_RESULT.earning.unit[1]
              )}
              desc={ASSESSMENT_RESULT.earning.label}
              type={schemeIndicator.earning >= 0 ? 'up' : 'down'}
            />
          </Col>
        </Row>
        {currentFeeTime === FeeTimeOptions.longTime && (
          <LineChart
            style={{ width: '100%', height: 260, marginTop: 41 }}
            data={chartData}
            xAxisField="time"
            xAxisName={ASSESSMENT_RESULT.schemeTwo.lineChart.xAxisName}
            yAxisField="cost"
            title={ASSESSMENT_RESULT.schemeTwo.lineChart.title}
            series={[
              {
                name: ASSESSMENT_RESULT.schemeTwo.lineChart.common,
                key: 'common',
              },
              { name: ASSESSMENT_RESULT.schemeTwo.lineChart.zzql, key: 'zzql' },
            ]}
            xAxisLabelFormatter={(val) =>
              val + ASSESSMENT_RESULT.schemeTwo.lineChart.xAxisName
            }
            yAxisLabelFormatter={(val) => formatCostUnit(val, 0)}
            tooltipFormatter={(params) => {
              let res =
                `<div><div>` +
                translateYearToMonth(params[0].axisValue) +
                `</div>`;
              params.forEach((item) => {
                res +=
                  '<div>' +
                  item.marker +
                  item.seriesName +
                  `&nbsp;` +
                  `<span>` +
                  formatCostUnit(item.value[1], 0) +
                  `</span></div>`;
              });
              return res + `</div>`;
            }}
          />
        )}
      </div>
    </div>
  );
};

export default forwardRef(AssessmentResult);
